import { Center, useDisclosure, useToast } from '@chakra-ui/react';
import { PatchDataWrapper } from '@diamond/shared/data-access';
import { BackofficeOrderDetail } from '@diamond/shared/types';
import { Button, Modal, showToast, Text } from '@diamond/shared/ui';
import {
  LIST_FEATURE_FLAGS,
  useCrossTabLock,
  useFeatureFlag,
} from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useActivitiesDetail } from '../../api';
import { useSubmitOrderWithValidation } from '../../hooks';
import { useActivityAction } from './action-context';

const SUBMIT_ORDER_URL = 'backoffice/order/submit';
const DEFAULT_BLOCK_UI_TEXT = 'Memproses… Mohon tunggu.';
const SUBMIT_TEXT = 'Memproses, submit order...';
const VALIDATE_TEXT = 'Memproses, voucher validation...';

type SubmitButtonProps = {
  activitiesId: string;
  refetch: () => Promise<unknown> | unknown;
  queryString: {
    order_by: string;
    direction: string;
    size: number;
    page: number;
  };
};

export function SubmitButton({
  activitiesId,
  refetch,
  queryString,
}: SubmitButtonProps) {
  const toast = useToast();
  const session = useAuthStore();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(false);
  const [inFlight, setInFlight] = useState(false);
  const [waitingForStatus, setWaitingForStatus] = useState(false);
  const waitingTimeoutRef = useRef<number | null>(null);
  const { submitBusy, cancelBusy, setSubmitBusy, setBlockUI, setBlockUIText } =
    useActivityAction();

  const cleanupSubmitState = async (
    useWaitingStatus = true,
    shouldRelease = false
  ) => {
    setIsLoading(false);
    setInFlight(false);
    if (useWaitingStatus) {
      setWaitingForStatus(true);
      setSubmitBusy(false);
    } else {
      setSubmitBusy(false);
    }
    setBlockUI(false);
    setBlockUIText(DEFAULT_BLOCK_UI_TEXT);
    if (shouldRelease) {
      release();
    }
    await refetch();
  };

  const selectStatus = useCallback(
    (d: BackofficeOrderDetail) => d.order.status,
    []
  );
  const { activitiesDetailData: statusData } = useActivitiesDetail(
    activitiesId,
    queryString,
    { select: selectStatus }
  );

  // Feature flag check
  const isVoucherValidationEnabled = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_VOUCHERS_MENU,
    false
  );

  const {
    submitOrderWithValidation,
    loadingStep,
    isLoading: isVoucherFlowLoading,
  } = useSubmitOrderWithValidation();

  const isDisable = statusData !== 'submitted';

  const { patchData: submitOrder } = PatchDataWrapper(
    SUBMIT_ORDER_URL,
    session.access_token,
    'admin'
  );

  const { locked, isOwner, acquire, release } = useCrossTabLock(
    'order-submit',
    activitiesId,
    { ttlMs: 5000, heartbeatMs: 1500 }
  );

  // Read peer lock (cancel) to cross-disable safely
  const { locked: cancelLocked, isOwner: cancelIsOwner } = useCrossTabLock(
    'order-cancel',
    activitiesId,
    { ttlMs: 5000, heartbeatMs: 1500 }
  );

  // Fallback cooldown: force-release after 30s
  useEffect(() => {
    if (!waitingForStatus) return;
    if (waitingTimeoutRef.current) {
      window.clearTimeout(waitingTimeoutRef.current);
    }
    waitingTimeoutRef.current = window.setTimeout(() => {
      setWaitingForStatus(false);
      setSubmitBusy(false);
      release();
      waitingTimeoutRef.current = null;
    }, 30000);
    return () => {
      if (waitingTimeoutRef.current) {
        window.clearTimeout(waitingTimeoutRef.current);
        waitingTimeoutRef.current = null;
      }
    };
  }, [waitingForStatus, release, setSubmitBusy]);

  // Release when status flips away from 'submitted'
  useEffect(() => {
    if (!waitingForStatus) return;
    if (statusData && statusData !== 'submitted') {
      setWaitingForStatus(false);
      setSubmitBusy(false);
      release();
      if (waitingTimeoutRef.current) {
        window.clearTimeout(waitingTimeoutRef.current);
        waitingTimeoutRef.current = null;
      }
    }
  }, [statusData, waitingForStatus, release, setSubmitBusy]);

  useEffect(() => {
    return () => {
      if (waitingTimeoutRef.current) {
        window.clearTimeout(waitingTimeoutRef.current);
        waitingTimeoutRef.current = null;
      }
      if (!waitingForStatus) {
        setSubmitBusy(false);
        release();
      }
    };
  }, [release, waitingForStatus, setSubmitBusy]);

  useEffect(() => {
    if (!isVoucherValidationEnabled) {
      return;
    }
    if (!submitBusy || !isVoucherFlowLoading) {
      return;
    }
    if (loadingStep === 'validating') {
      setBlockUIText(VALIDATE_TEXT);
      return;
    }
    if (loadingStep === 'submitting') {
      setBlockUIText(SUBMIT_TEXT);
    }
  }, [
    isVoucherValidationEnabled,
    submitBusy,
    isVoucherFlowLoading,
    loadingStep,
    setBlockUIText,
  ]);

  const handleOpen = () => onOpen();

  const handleConfirm = async () => {
    if (inFlight || isLoading || (locked && !isOwner)) {
      onClose();
      showToast(toast, 'info', 'Order sedang diproses pada tab lain.');
      return;
    }
    const gotLock = acquire();
    if (!gotLock) {
      onClose();
      showToast(toast, 'info', 'Order sedang diproses pada tab lain.');
      return;
    }
    setIsLoading(true);
    setInFlight(true);
    setSubmitBusy(true);
    setBlockUIText(SUBMIT_TEXT);
    setBlockUI(true);
    onClose();

    // 1. Legacy Flow (Feature Flag OFF)
    if (!isVoucherValidationEnabled) {
      try {
        await submitOrder('', activitiesId);
        showToast(toast, 'success', 'Berhasil Submit Order');
        await cleanupSubmitState(true, false);
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : 'Gagal Submit Order';

        // Sentry already captured by PatchDataWrapper(..., 'admin')
        showToast(toast, 'error', errorMessage);
        await cleanupSubmitState(false, true);
      }
      return;
    }

    // 2. New Flow with Validation (Feature Flag ON)
    try {
      await submitOrderWithValidation({ id: activitiesId });
      showToast(toast, 'success', 'Berhasil Submit Order');
      await cleanupSubmitState(true, false);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Gagal Submit Order';
      showToast(toast, 'error', errorMessage);
      await cleanupSubmitState(false, true);
    }
  };

  const peerCancelLockActive = cancelLocked && !cancelIsOwner;
  const submitDisabled =
    isDisable ||
    (locked && !isOwner) ||
    isLoading ||
    inFlight ||
    waitingForStatus ||
    submitBusy ||
    cancelBusy ||
    peerCancelLockActive;

  return (
    <>
      <Button
        px="4"
        mx="2"
        variant="solid"
        onClick={handleOpen}
        isDisabled={submitDisabled}
        isLoading={submitBusy}
        aria-busy={submitBusy}
      >
        Submit
      </Button>
      <Modal
        name="submit_order"
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={handleConfirm}
        isLoading={isLoading}
        labelSubmit="Oke"
      >
        <Center>
          <Text m="10" align="center" fontSize="2xl" as="b">
            Submit Order?
          </Text>
        </Center>
      </Modal>
    </>
  );
}

export default SubmitButton;
